/**
 * FighterDrone.ts
 * Pooled hostile fighter for the World 2 space shmup.
 *
 * Behaviour (this slice): linear leftward drift in world space. Spawned at
 * the right edge of the camera, drifts past the ship at
 * `SPACE_AUTO_SCROLL_SPEED + FIGHTER_DRONE_DRIFT_SPEED` apparent on-screen
 * velocity, and despawns once its right edge crosses
 * `SPACE_ENEMY_OFFSCREEN_MARGIN` past the left edge of the camera. Dies in
 * one ship-projectile hit (`FIGHTER_DRONE_HP = 1`). Also fires a single
 * left-travelling laser bolt on a per-drone cadence once it has cleared
 * the spawn margin and is visible inside the viewport.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module FighterDrone
 */

import { SpaceEnemyBase } from './SpaceEnemyBase';
import { spaceState } from '../engine/spaceState';
import { fireFighterDroneLaser } from '../engine/fighterDroneFire';
import type { LevelRuntime } from '../engine/gameState';
import { VIEWPORT_WIDTH } from '../../constants/gameConstants';
import {
  FIGHTER_DRONE_WIDTH,
  FIGHTER_DRONE_HEIGHT,
  FIGHTER_DRONE_HP,
  FIGHTER_DRONE_DRIFT_SPEED,
  FIGHTER_DRONE_WAVE_AMPLITUDE_PX,
  FIGHTER_DRONE_WAVE_FREQUENCY_HZ,
  FIGHTER_DRONE_FIRE_INTERVAL_S,
  FIGHTER_DRONE_FIRE_INITIAL_DELAY_S,
  FIGHTER_DRONE_LASER_HEIGHT,
  SPACE_ENEMY_OFFSCREEN_MARGIN,
  type SpaceEnemyKind,
} from '../../constants/spaceEnemyConstants';

/**
 * Sine-wave left-drifting fighter drone. Pooled — use after `acquire()` from
 * the enemy pool in `spaceEnemySystem`. The drone drifts left at a constant
 * `vx` but its Y oscillates around the spawn lane so the player must move
 * vertically to evade.
 */
export class FighterDrone extends SpaceEnemyBase {
  /** Discriminator read by the renderer to pick a colour / sprite. */
  public readonly kind: SpaceEnemyKind = 'fighter';

  /** Per-spawn phase offset (radians) so a wave of identical drones doesn't sync. */
  private phase = 0;
  /** Local time accumulator for the sine driver. */
  private localT = 0;
  /**
   * Seconds remaining until the next laser shot. Seeded by `spawn()`
   * with `FIGHTER_DRONE_FIRE_INITIAL_DELAY_S` plus a per-drone phase
   * offset so a wave of drones doesn't sync-fire. Reset to
   * `FIGHTER_DRONE_FIRE_INTERVAL_S` after each successful shot.
   */
  private fireTimer = 0;

  constructor() {
    super(FIGHTER_DRONE_WIDTH, FIGHTER_DRONE_HEIGHT);
  }

  /**
   * Initialise the drone at a world position. Marks it live and assigns a
   * fresh `spawnId` via the base class. The X drift is constant; the Y
   * coordinate oscillates around `y` via a sine wave whose phase is derived
   * from `spawnId` so neighbouring drones don't share a Y at any given t.
   *
   * @param x - Spawn X (world space, top-left of hitbox).
   * @param y - Spawn Y baseline (world space, top-left of hitbox).
   */
  public spawn(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.vx = -FIGHTER_DRONE_DRIFT_SPEED;
    this.vy = 0;
    this.hp = FIGHTER_DRONE_HP;
    this.markSpawned();
    // Derive a stable per-spawn phase from the post-spawn `spawnId`
    // so two drones spawned the same frame don't fly in lock-step.
    this.phase = (this.spawnId % 16) * (Math.PI / 8);
    // DECISION: Offset `localT` so the very first sine sample is zero,
    // i.e. `sin(2πf * localT + phase) = 0` at t=0. Combined with the
    // delta integrator below, this guarantees the spawn `y` IS the
    // wobble centre (otherwise a non-zero initial sin(phase) shifts the
    // band by up to ±amplitude and the spawn clamp's `±amplitude` reach
    // is half what it needs to be — PR #124 review).
    this.localT = -this.phase / (2 * Math.PI * FIGHTER_DRONE_WAVE_FREQUENCY_HZ);
    // DECISION: Seed the fire timer with the initial delay plus a small
    // per-spawn jitter (re-using `this.phase` as a free entropy source
    // bounded by ~0–2π) so a wave of drones spawned the same tick
    // doesn't fire on identical frames downstream.
    this.fireTimer =
      FIGHTER_DRONE_FIRE_INITIAL_DELAY_S +
      (this.phase / (2 * Math.PI)) * FIGHTER_DRONE_FIRE_INTERVAL_S;
  }

  /**
   * Advance one fixed timestep. Linear X integration + sinusoidal Y delta
   * applied on top of current Y + camera-relative left cull.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused (space has no level geometry).
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    // DECISION: Drive Y by the *delta* between successive sine samples so
    // any external `y` reassignment (e.g. test fixtures, future homing AI)
    // is preserved as a new drift centre. Equivalent total trajectory as
    // `y = baseY + sin(...)*amp` over a closed loop, but composable.
    const prevAngle =
      2 * Math.PI * FIGHTER_DRONE_WAVE_FREQUENCY_HZ * this.localT + this.phase;
    this.localT += dt;
    const nextAngle =
      2 * Math.PI * FIGHTER_DRONE_WAVE_FREQUENCY_HZ * this.localT + this.phase;
    this.x += this.vx * dt;
    this.y +=
      (Math.sin(nextAngle) - Math.sin(prevAngle)) *
      FIGHTER_DRONE_WAVE_AMPLITUDE_PX;

    // DECISION: Camera-relative left cull on the RIGHT edge of the drone so a
    // drone whose left edge has crossed the camera left edge does not vanish
    // while still partially on-screen. Mirrors AsteroidEntity.
    const leftCullX = spaceState.cameraX - SPACE_ENEMY_OFFSCREEN_MARGIN;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
      return;
    }

    // Tick the fire cadence. Only fire once the drone has drifted INTO the
    // viewport (its left edge is to the left of the camera's right edge)
    // so bolts spawned while still in the right-side spawn margin don't
    // appear from nowhere. The bolt muzzle sits at the drone's left edge,
    // vertically centred on the drone hitbox.
    this.fireTimer -= dt;
    if (this.fireTimer <= 0) {
      const visibleRightX = spaceState.cameraX + VIEWPORT_WIDTH;
      if (this.x < visibleRightX) {
        const fired = fireFighterDroneLaser(
          this.x,
          this.y + this.height / 2 - FIGHTER_DRONE_LASER_HEIGHT / 2,
        );
        // Pool miss: leave the timer negative so we retry next tick
        // rather than burning a full cadence on a dropped shot.
        if (!fired) return;
      }
      this.fireTimer = FIGHTER_DRONE_FIRE_INTERVAL_S;
    }
  }
}
